import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trophy, Medal, Loader2, Gift, Ticket, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Leaderboard — top tap-in champions. Organiser can "Reward top-3" which
 * mints TAP-XXXX codes and auto-DMs winners. Reward chips appear on the podium.
 */
export function Leaderboard({ eventId, isOwner }) {
  const [rows, setRows]           = useState([]);
  const [totalTaps, setTotalTaps] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [issuing, setIssuing]     = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/nfc/events/${eventId}/leaderboard`, { params: { limit: 20 } });
      setRows(r.data.leaderboard || []);
      setTotalTaps(r.data.total_taps || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 10000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const issueRewards = async () => {
    if (!window.confirm("Issue reward codes to the top-3 tap champions? They'll each receive a DM with the code.")) return;
    setIssuing(true);
    try {
      const r = await api.post(`/nfc/events/${eventId}/rewards/issue`, { top_n: 3 });
      toast.success(`Issued ${r.data.count} reward codes · winners have been DM'd`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reward issuance failed");
    } finally { setIssuing(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const podium = useMemo(() => rows.slice(0, 3), [rows]);
  const rest   = useMemo(() => rows.slice(3), [rows]);

  return (
    <section data-testid="event-leaderboard" className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
      <div className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-stone-200 flex items-center gap-2 flex-wrap">
        <Trophy size={13} className="text-amber-700"/>
        <span className="text-[11px] uppercase tracking-widest font-black text-stone-700">Tap champions</span>
        <span className="text-[11px] text-stone-500 ml-auto">{totalTaps} taps total</span>
        {isOwner && rows.length > 0 && (
          <Button
            onClick={issueRewards}
            disabled={issuing}
            size="sm"
            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white font-black h-7 text-[11px]"
            data-testid="issue-rewards-btn"
            title="Auto-DM top-3 champions with a discount code"
          >
            {issuing ? <Loader2 size={11} className="animate-spin mr-1"/> : <Gift size={11} className="mr-1"/>}
            Reward top-3
          </Button>
        )}
      </div>
      {loading ? (
        <div className="p-6 text-center text-sm text-stone-500"><Loader2 className="inline animate-spin mr-1" size={13}/> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-stone-500">No taps yet — the leaderboard fills up as attendees arrive.</div>
      ) : (
        <div>
          {podium.length > 0 && (
            <div className="grid grid-cols-3 gap-2 p-3 bg-gradient-to-b from-amber-50/50 to-white">
              {podium.map((r, i) => {
                const styles = [
                  { podium: "bg-gradient-to-br from-amber-400 to-yellow-500",  size: "w-16 h-16", medal: "text-amber-900"  },
                  { podium: "bg-gradient-to-br from-slate-300 to-slate-400",   size: "w-14 h-14", medal: "text-slate-700"  },
                  { podium: "bg-gradient-to-br from-orange-400 to-orange-600", size: "w-14 h-14", medal: "text-orange-900" },
                ][i];
                return (
                  <div key={r.user_id} className="text-center" data-testid={`leaderboard-podium-${r.rank}`}>
                    {r.user_pic ? (
                      <img src={r.user_pic} alt={r.user_name} className={`${styles.size} rounded-full object-cover mx-auto border-4 border-white shadow-lg`}/>
                    ) : (
                      <div className={`${styles.size} ${styles.podium} rounded-full grid place-items-center mx-auto text-white font-black text-lg shadow-lg`}>
                        {(r.user_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                    )}
                    <Medal size={16} className={`mx-auto mt-1 ${styles.medal}`}/>
                    <div className="text-xs font-black text-stone-900 truncate mt-0.5">{r.user_name}</div>
                    <div className="text-[10px] text-stone-500 font-mono">{r.tap_count} taps</div>
                    {r.reward_code && (
                      <button
                        onClick={() => copyCode(r.reward_code)}
                        className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-900 text-[10px] font-black px-2 py-0.5"
                        data-testid={`reward-code-${r.rank}`}
                        title="Click to copy"
                      >
                        <Ticket size={9}/> {r.reward_code} · {r.reward_discount}% <Copy size={9}/>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {rest.length > 0 && (
            <ul className="divide-y divide-stone-100">
              {rest.map(r => (
                <li key={r.user_id} className="p-3 flex items-center gap-3" data-testid={`leaderboard-row-${r.rank}`}>
                  <div className="w-7 h-7 rounded-full bg-stone-100 grid place-items-center text-xs font-black text-stone-600">{r.rank}</div>
                  {r.user_pic ? (
                    <img src={r.user_pic} alt={r.user_name} className="w-9 h-9 rounded-full object-cover"/>
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-amber-50 grid place-items-center text-amber-700 font-black text-sm">
                      {(r.user_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0 font-bold text-stone-900 truncate">{r.user_name}</div>
                  <div className="text-xs font-black text-amber-700">{r.tap_count} <span className="text-stone-400 font-normal">taps</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
